import React from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { WorkloadGaugeScale } from '../interactions/WorkloadGaugeScale.jsx'
import { ToggleChip } from '../../../ui/ToggleChip/ToggleChip.jsx'
import { CardHistoryBack } from '../interactions/CardHistoryBack.jsx'
import { useCardHistory } from '../interactions/useCardHistory.js'

const CARD_DEF = { id: 'prep-workload', icon: '▣', label: 'Workload' }

/**
 * Workload source chips — pending full chip set from design owner (copy-decisions §open-items).
 * Using a provisional set per "meetings, focused work, on-call…" design anatomy.
 */
const SOURCE_CHIPS = [
  { id: 'meetings',     label: 'meetings' },
  { id: 'focused-work', label: 'focused work' },
  { id: 'on-call',      label: 'on-call' },
  { id: 'reviews',      label: 'reviews' },
  { id: 'admin',        label: 'admin' },
  { id: 'incidents',    label: 'incidents' },
]

/**
 * WorkloadCard — semicircle dial (0–100) + source chips + free-text note.
 * catalog #20, prep-workload. Extends WorkloadGaugeScale with source chips + note.
 */
export function WorkloadCard({ card, value, onChange, disabled, ownerUserId }) {
  const { data: historyData, loading: historyLoading, error: historyError } = useCardHistory(card?.prepCardId ?? 'prep-workload', ownerUserId)

  const back = (
    <CardHistoryBack
      cardDef={CARD_DEF}
      entries={historyData?.entries ?? []}
      loading={historyLoading}
      error={historyError}
      onFlip={() => {}}
    />
  )

  const sources = value?.sources ?? []
  const note = value?.note ?? ''

  function handleGaugeChange(newVal) {
    onChange({ ...newVal, sources, note })
  }

  function toggleSource(id) {
    if (disabled) return
    const next = sources.includes(id) ? sources.filter((s) => s !== id) : [...sources, id]
    onChange({ ...(value ?? {}), sources: next, note })
  }

  function handleNoteChange(e) {
    onChange({ ...(value ?? {}), sources, note: e.target.value })
  }

  const hasFill = value?.value != null

  return (
    <CardFrame
      kind="prep"
      icon="▣"
      iconClass="mustard"
      title="Workload"
      prompt="How loaded are you?"
      state={disabled ? 'locked' : hasFill ? 'filled' : 'empty'}
      back={back}
      data-testid="prep-card-prep-workload"
      className="prepcard"
    >
      <WorkloadGaugeScale value={value} onChange={handleGaugeChange} disabled={disabled} />
      <div style={styles.sources}>
        <span style={styles.sourcesLabel}>What&apos;s pushing it?</span>
        <div style={styles.chipRow} role="group" aria-label="Workload sources">
          {SOURCE_CHIPS.map((chip) => (
            <ToggleChip
              key={chip.id}
              selected={sources.includes(chip.id)}
              disabled={disabled}
              onClick={() => toggleSource(chip.id)}
            >
              {chip.label}
            </ToggleChip>
          ))}
        </div>
        {hasFill && (
          <textarea
            rows={1}
            value={note}
            placeholder={disabled ? '' : 'Anything behind this…'}
            disabled={disabled}
            onChange={handleNoteChange}
            style={styles.note}
            aria-label="Workload note"
          />
        )}
      </div>
    </CardFrame>
  )
}

const styles = {
  sources: { padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  sourcesLabel: { fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  note: {
    width: '100%', border: 'none', borderBottom: '1px solid var(--color-line)',
    outline: 'none', background: 'transparent', fontSize: 12,
    color: 'var(--color-text-secondary)', fontFamily: 'inherit',
    padding: '2px 0', resize: 'none', overflow: 'hidden', lineHeight: 1.5,
  },
}
